import { useAuth } from '../contexts/AuthContext';
import { ROUTE_PERMISSIONS, ROLE_FEATURES } from '../constants/permissions';
import { ROLES, hasRoleLevel, isOneOfRoles } from '../constants/roles';

/**
 * Enhanced hook for checking permissions, features, and roles
 * Provides convenient methods for permission-based and role-based UI control
 */
export const usePermissions = () => {
    const { permissions, features, hasPermission, hasAnyPermission, hasFeature, role } = useAuth();

    /**
     * Check if user has all specified permissions
     * @param {string[]} requiredPermissions - Array of permission strings
     * @returns {boolean}
     */
    const hasAllPermissions = (requiredPermissions) => {
        if (!Array.isArray(requiredPermissions)) {
            return hasPermission(requiredPermissions);
        }
        return requiredPermissions.every(permission => hasPermission(permission));
    };

    /**
     * Check if user has at least one of the specified permissions
     * @param {string[]} requiredPermissions - Array of permission strings
     * @returns {boolean}
     */
    const hasAnyOf = (requiredPermissions) => {
        if (!Array.isArray(requiredPermissions)) {
            return hasPermission(requiredPermissions);
        }
        return hasAnyPermission(requiredPermissions);
    };

    /**
     * Check if user can access a specific route
     * @param {string} route - Route path
     * @returns {boolean}
     */
    const canAccessRoute = (route) => {
        const requiredPermissions = ROUTE_PERMISSIONS[route];
        if (!requiredPermissions) return true; // No permissions required
        return hasAnyOf(requiredPermissions);
    };

    /**
     * Check if user can use a specific page feature
     * @param {string[]} requiredPermissions - Required permissions for the feature
     * @returns {boolean}
     */
    const canUseFeature = (requiredPermissions) => {
        if (!requiredPermissions || requiredPermissions.length === 0) return true;
        return hasAnyOf(requiredPermissions);
    };

    // ==================== ROLE-BASED METHODS ====================

    /**
     * Check if user has a specific role
     * @param {string} requiredRole - Role code (e.g., 'SA', 'CA')
     * @returns {boolean}
     */
    const hasRole = (requiredRole) => {
        return role === requiredRole;
    };

    /**
     * Check if user has any of the specified roles
     * @param {string[]} allowedRoles - Array of role codes
     * @returns {boolean}
     */
    const hasAnyRole = (allowedRoles) => {
        if (!role) return false;
        return isOneOfRoles(role, allowedRoles);
    };

    /**
     * Check if user has at least the required role level
     * Example: hasMinimumRole(ROLES.CENTER_DIRECTOR) returns true for CTD, CTA, CA, SA
     * @param {string} minimumRole - Minimum required role
     * @returns {boolean}
     */
    const hasMinimumRole = (minimumRole) => {
        if (!role) return false;
        return hasRoleLevel(role, minimumRole);
    };

    /**
     * Check if user can access a feature based on their role
     * Uses ROLE_FEATURES mapping
     * @param {string} page - Page name (e.g., 'LEAD_PAGE')
     * @param {string} feature - Feature name (e.g., 'CREATE_LEAD')
     * @returns {boolean}
     */
    const canUseFeatureByRole = (page, feature) => {
        const allowedRoles = ROLE_FEATURES[page]?.[feature];
        if (!allowedRoles || allowedRoles.length === 0) return true;
        return hasAnyRole(allowedRoles);
    };

    /**
     * Check if user is an admin (SA, CA, or CTA)
     * @returns {boolean}
     */
    const isAdmin = () => {
        return hasAnyRole([ROLES.SYSTEM_ADMIN, ROLES.CUSTOMER_ADMIN, ROLES.CENTER_ADMIN]);
    };

    /**
     * Check if user is a staff member (SA, CA, CTA, CTD, or TU)
     * @returns {boolean}
     */
    const isStaff = () => {
        return hasAnyRole([
            ROLES.SYSTEM_ADMIN,
            ROLES.CUSTOMER_ADMIN,
            ROLES.CENTER_ADMIN,
            ROLES.CENTER_DIRECTOR,
            ROLES.TUTOR,
        ]);
    };

    return {
        // Permission-based methods
        permissions,
        features,
        hasPermission,
        hasAnyPermission,
        hasFeature,
        hasAllPermissions,
        hasAnyOf,
        canAccessRoute,
        canUseFeature,

        // Role-based methods
        role,
        hasRole,
        hasAnyRole,
        hasMinimumRole,
        canUseFeatureByRole,
        isAdmin,
        isStaff,
    };
};
